/**
 * @fileoverview Soft radial gradient defs for donut slices. Each slice color
 * fades from slightly dim at the ring's inner edge to full color at the outer
 * rim — a consistent "lit tube" effect that follows the ring geometry (unlike
 * a linear fade, whose direction would differ per slice bounding box).
 * @module shared/components/ui/DonutSliceGradients
 */

import { ReactElement } from 'react'

interface DonutSliceGradientsProps {
  /** Unique per chart instance — gradient ids are global to the document. */
  idPrefix: string
  /** Slice colors in render order (CSS color values or var() references). */
  colors: string[]
}

/**
 * Renders the `<defs>` block for donutSliceGradientFill references (from
 * `@/shared/utils/donutGradient`). Place inside the recharts `<PieChart>`;
 * pair each `<Cell>`'s fill with `donutSliceGradientFill(idPrefix, index)`.
 *
 * Geometry assumption: the chart is square with the pie centered, using
 * `innerRadius="55%" outerRadius="70%"` — the gradient radius (35% of the
 * viewport) matches that outer radius, so the 55%→100% stop range spans
 * exactly the visible ring band.
 */
export function DonutSliceGradients({ idPrefix, colors }: DonutSliceGradientsProps): ReactElement {
  return (
    <defs>
      {colors.map((color, index) => (
        <radialGradient
          key={index}
          id={`${idPrefix}-${index}`}
          cx="50%"
          cy="50%"
          r="35%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="55%" stopColor={color} stopOpacity={0.55} />
          <stop offset="100%" stopColor={color} stopOpacity={1} />
        </radialGradient>
      ))}
    </defs>
  )
}
